import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Star, MapPin, Building2, Tag, Home, Navigation, ExternalLink, Globe, Instagram, Phone, ShoppingCart, FileText, Package, Eye, Download, ChevronRight, Store, Calendar, Layers, Clock, ZoomIn, ChevronLeft, Map, ExternalLink as ExtLink, Award, Heart, Target, Trophy } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getImageUrl, getFileUrl, getProjectFull, getSuccessStoryFull } from '../../lib/api';

const FALLBACK = 'https://images.unsplash.com/photo-1694018359679-49465b4c0d61?w=900&h=500&fit=crop&q=80';
const Snap = ({ s = 18 }) => <svg viewBox="0 0 24 24" width={s} height={s} fill="currentColor"><path d="M12.206.793c.99 0 4.347.276 5.93 3.521.529 1.097.403 2.578.371 3.157l-.004.045c-.081 1.033-.262 2.162-.573 3.27-.375 1.336-.892 2.555-1.526 3.52a9.41 9.41 0 0 1-1.396 1.673c-.735.686-1.622 1.267-2.638 1.267-.658 0-1.117-.168-1.452-.335a3.5 3.5 0 0 1-.559-.321c-.364-.254-.756-.431-1.184-.454a2.91 2.91 0 0 0-1.191.397 4.58 4.58 0 0 1-.552.262c-.31.11-.696.195-1.164.195-1.019 0-1.907-.605-2.643-1.293a9.8 9.8 0 0 1-1.392-1.68c-.633-.968-1.148-2.19-1.523-3.53a14.44 14.44 0 0 1-.573-3.266l-.004-.045c-.032-.58-.158-2.06.371-3.157C2.248 1.07 5.604.793 6.595.793c.486 0 1.634.163 2.774.98.458.328.78.647.981.853.201-.206.523-.525.981-.853C12.47.956 13.619.793 14.205.793Z"/></svg>;
const STS = { published: ['منشور', '#2FAE8F'], active: ['نشط', '#2FAE8F'], draft: ['مسودة', '#D6A84F'], pending: ['قيد المراجعة', '#D6A84F'], inactive: ['متوقف', '#EF4444'] };
const REV = { approved: ['معتمد', '#2FAE8F'], pending: ['بانتظار المراجعة', '#D6A84F'], rejected: ['مرفوض', '#EF4444'] };

const ProjectDetailPanel = ({ project, onClose, onViewProject }) => {
  const { language } = useLanguage();
  const [full, setFull] = useState(null);
  const [view, setView] = useState('summary');
  const [lightbox, setLightbox] = useState(-1);

  const isStory = project?._type === 'success_story';

  useEffect(() => {
    if (!project?.id) { setFull(null); setView('summary'); return; }
    setView('summary');
    if (isStory) {
      getSuccessStoryFull(project.id).then(r => setFull(r.data)).catch(() => setFull(null));
    } else {
      getProjectFull(project.id).then(r => setFull(r.data)).catch(() => setFull(null));
    }
  }, [project?.id, isStory]);

  useEffect(() => {
    if (!project) return;
    const h = (e) => {
      if (e.key === 'Escape') {
        if (lightbox >= 0) setLightbox(-1);
        else if (view === 'details') setView('summary');
        else onClose();
      }
      if (lightbox >= 0) {
        if (e.key === 'ArrowLeft') setLightbox(p => (p + 1) % imgs.length);
        if (e.key === 'ArrowRight') setLightbox(p => (p - 1 + imgs.length) % imgs.length);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  if (!project) return null;

  /* ═══ SUCCESS STORY MODE ═══ */
  if (isStory) {
    const s = full || project;
    const stName = s.title_ar || project.title_ar || '';
    const stHook = s.short_hook || project.short_hook || '';
    const stChallenge = s.challenge_text || '';
    const stMetrics = s.result_metrics || '';
    const stSupport = s.support_types || [];
    const stCity = s.city || project.city || '';
    const stRegion = s.region || project.region || '';
    const stSector = s.sector || '';
    const stCover = s.cover_image ? (s.cover_image.startsWith('http') ? s.cover_image : getImageUrl(s.cover_image)) : FALLBACK;
    const stGallery = s.gallery_images || [];
    const relProj = s.related_project_summary || null;

    /* ── STORY DETAIL — Premium single-page layout ── */
    if (view === 'details') return <StoryPremiumDetail
      stName={stName} stHook={stHook} stChallenge={stChallenge} stMetrics={stMetrics}
      stSupport={stSupport} stCity={stCity} stRegion={stRegion} stSector={stSector}
      stCover={stCover} stGallery={stGallery} galleryTags={s.gallery_tags || []}
      relProj={relProj} s={s}
      onClose={onClose} onBack={() => setView('summary')} onViewProject={onViewProject}
      lightbox={lightbox} setLightbox={setLightbox}
    />;

    /* ── STORY SUMMARY POPUP ── */
    return (
      <>
        <div className="px-overlay" data-testid="story-modal-backdrop" onClick={onClose} />
        <div className="px-popup" dir="rtl" data-testid="story-modal">
          <button className="px-x px-x-sm" data-testid="story-modal-close" onClick={onClose}><X size={16} /></button>
          <div className="px-pop-top">
            <div className="px-pop-logo"><div className="px-pop-logo-fb" style={{ background: '#E8A838' }}><Award size={18} color="#fff" /></div></div>
            <div className="px-pop-title-area">
              <h2 className="px-pop-name" data-testid="story-modal-name">{stName}</h2>
              <div className="px-pop-meta">
                <span className="px-pop-cat" style={{ borderColor: '#E8A83844', color: '#E8A838' }}>قصة نجاح</span>
                {stSector && <span className="px-pop-subcat">{stSector}</span>}
              </div>
            </div>
          </div>
          <div className="px-pop-mid">
            {stHook && <p className="px-pop-desc">{stHook}</p>}
            <div className="px-pop-loc">
              {stRegion && <span><Building2 size={12} />{stRegion}</span>}
              {stCity && <span><MapPin size={12} />{stCity}</span>}
            </div>
          </div>
          <div className="px-pop-btns">
            <button className="px-pop-btn-details" data-testid="story-details-btn" onClick={() => setView('details')}><Eye size={15} /> استكشف القصة</button>
            <button className="px-pop-btn-nav" data-testid="story-goto-btn" onClick={() => { if (s.latitude && s.longitude) window.open(`https://www.google.com/maps/dir/?api=1&destination=${s.latitude},${s.longitude}`, '_blank'); }} disabled={!s.latitude || !s.longitude}><Navigation size={15} /> الموقع</button>
          </div>
        </div>
      </>
    );
  }

  /* ═══ PROJECT MODE (existing code) ═══ */
  const p = full || project;
  const name = p.project_name_ar || project.project_name_ar;
  const nameEn = p.project_name_en || project.project_name_en || '';
  const catColor = project.categoryColor || '#2FAE8F';
  const logo = p.project_logo ? getImageUrl(p.project_logo) : null;
  const imgs = (p.images?.length ? p.images : project.images) || [];
  const hero = imgs.length ? getImageUrl(imgs[0]) : FALLBACK;
  const products = full?.products || [];
  const channels = full?.sales_channels || [];
  const desc = p.description_ar || p.description || project.description || '';
  const shortD = p.short_description_ar || p.short_description || project.short_description || '';
  const region = p.region?.name_ar || project.regionName || '';
  const city = p.city?.name_ar || project.cityName || '';
  const district = p.district?.name_ar || project.districtName || '';
  const cat = p.category?.name_ar || project.categoryName || '';
  const subcat = p.subcategory?.name_ar || project.subcategoryName || '';
  const featured = p.is_featured || project.is_featured;
  const tags = p.tags || project.tags || [];
  const hasSales = p.online_store_url || p.snapchat_url || p.instagram_url || p.mobile_number || (p.sales_location_lat && p.sales_location_lng) || channels.length > 0;
  const hasSalesLoc = p.sales_location_lat && p.sales_location_lng;
  const created = p.created_at ? new Date(p.created_at).toLocaleDateString('ar-SA') : '';
  const sts = STS[p.status] || [p.status, '#888'];
  const rev = REV[p.review_status] || [p.review_status, '#888'];

  /* ═══ LIGHTBOX ═══ */
  const Lightbox = () => lightbox >= 0 && imgs.length > 0 ? (
    <div className="px-lb" data-testid="lightbox" onClick={() => setLightbox(-1)}>
      <img src={getImageUrl(imgs[lightbox])} alt="" className="px-lb-img" onClick={e => e.stopPropagation()} />
      <button className="px-lb-close" onClick={() => setLightbox(-1)}><X size={22} /></button>
      {imgs.length > 1 && <>
        <button className="px-lb-prev" onClick={e => { e.stopPropagation(); setLightbox((lightbox - 1 + imgs.length) % imgs.length); }}><ChevronRight size={24} /></button>
        <button className="px-lb-next" onClick={e => { e.stopPropagation(); setLightbox((lightbox + 1) % imgs.length); }}><ChevronLeft size={24} /></button>
      </>}
      <span className="px-lb-counter">{lightbox + 1} / {imgs.length}</span>
    </div>
  ) : null;

  /* ═══ FULL DETAILS ═══ */
  if (view === 'details') return (
    <>
      <div className="px-overlay" data-testid="project-full-backdrop" onClick={() => setView('summary')} />
      <div className="px-full" dir="rtl" data-testid="project-full-modal">
        <button className="px-x" data-testid="project-full-close" onClick={onClose}><X size={20} /></button>
        <div className="px-full-scroll">

          {/* ── 1. HEADER ── */}
          <div className="px-hdr">
            <div className="px-hdr-bg"><img src={hero} alt="" /><div className="px-hdr-grad" /></div>
            <div className="px-hdr-inner">
              <button className="px-back" data-testid="project-full-back" onClick={() => setView('summary')}><ChevronRight size={16} /> رجوع للملخص</button>
              <div className="px-hdr-row">
                <div className="px-hdr-logo">{logo ? <img src={logo} alt="" /> : <div className="px-hdr-logo-fb" style={{ background: catColor }}><Tag size={28} color="#fff" /></div>}</div>
                <div className="px-hdr-text">
                  <h1 className="px-hdr-name">{name}</h1>
                  {nameEn && <span className="px-hdr-name-en">{nameEn}</span>}
                  <div className="px-hdr-badges">
                    <span className="px-hdr-badge" style={{ '--bc': sts[1] }}>{sts[0]}</span>
                    {featured && <span className="px-hdr-badge" style={{ '--bc': '#D6A84F' }}><Star size={11} fill="currentColor" /> مميز</span>}
                    {cat && <span className="px-hdr-badge-cat">{cat}</span>}
                    {subcat && <span className="px-hdr-badge-sub">{subcat}</span>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── BODY ── */}
          <div className="px-bd">

            {/* ── 2. GALLERY ── */}
            {imgs.length > 0 && (
              <section className="px-s px-an" style={{ '--d': '0s' }}>
                <h3 className="px-sh">معرض الصور</h3>
                <div className="px-gal">
                  {imgs.map((im, i) => (
                    <div key={i} className="px-gal-item" onClick={() => setLightbox(i)} data-testid={`gallery-img-${i}`}>
                      <img src={getImageUrl(im)} alt="" loading="lazy" />
                      <div className="px-gal-zoom"><ZoomIn size={18} /></div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ── 3. ABOUT ── */}
            {desc && (
              <section className="px-s px-an" style={{ '--d': '0.06s' }}>
                <h3 className="px-sh">عن المشروع</h3>
                <p className="px-sp">{desc}</p>
              </section>
            )}

            {/* ── TAGS (moved above location) ── */}
            {tags.length > 0 && (
              <section className="px-s px-an" style={{ '--d': '0.08s' }}>
                <h3 className="px-sh"><Tag size={15} /> الكلمات المفتاحية</h3>
                <div className="px-tags-wrap">{tags.map((t, i) => <span key={i} className="px-tag-pill">{t}</span>)}</div>
              </section>
            )}

            {/* ── 4. LOCATION & CLASSIFICATION ── */}
            <section className="px-s px-an" style={{ '--d': '0.1s' }}>
              <h3 className="px-sh"><MapPin size={15} /> الموقع والتصنيف</h3>
              <div className="px-detail-grid">
                {region && <div className="px-dg-item"><span className="px-dg-l">المنطقة</span><span className="px-dg-v">{region}</span></div>}
                {city && <div className="px-dg-item"><span className="px-dg-l">المدينة</span><span className="px-dg-v">{city}</span></div>}
                {cat && <div className="px-dg-item"><span className="px-dg-l">التصنيف</span><span className="px-dg-v">{cat}</span></div>}
                {subcat && <div className="px-dg-item"><span className="px-dg-l">التصنيف الفرعي</span><span className="px-dg-v">{subcat}</span></div>}
                {district && <div className="px-dg-item"><span className="px-dg-l">الحي</span><span className="px-dg-v">{district}</span></div>}
              </div>
            </section>

            {/* ── 5. SALES LOCATION MAP ── */}
            {hasSalesLoc && (
              <section className="px-s px-an" style={{ '--d': '0.14s' }}>
                <h3 className="px-sh"><Map size={15} /> موقع البيع</h3>
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${p.sales_location_lat},${p.sales_location_lng}`} target="_blank" rel="noopener noreferrer" className="px-map-card" data-testid="pf-map-card">
                  <div className="px-map-preview">
                    <img src={`https://maps.googleapis.com/maps/api/staticmap?center=${p.sales_location_lat},${p.sales_location_lng}&zoom=14&size=600x200&maptype=roadmap&style=feature:all|element:geometry|color:0x0a1628&style=feature:all|element:labels.text.fill|color:0x6b8e8e&style=feature:water|color:0x0e2a3a&markers=color:0x2FAE8F|${p.sales_location_lat},${p.sales_location_lng}&key=`} alt="" className="px-map-img" onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                    <div className="px-map-fallback" style={{ display: 'none' }}>
                      <MapPin size={32} />
                      <span>{p.sales_location_text || 'موقع البيع'}</span>
                      <span className="px-map-coords" dir="ltr">{Number(p.sales_location_lat).toFixed(5)}, {Number(p.sales_location_lng).toFixed(5)}</span>
                    </div>
                  </div>
                  {p.sales_location_text && <span className="px-map-address">{p.sales_location_text}</span>}
                  <div className="px-map-action"><Navigation size={14} /> فتح في خرائط قوقل <ExternalLink size={11} /></div>
                </a>
              </section>
            )}

            {/* ── 6. SALES CHANNELS ── */}
            {hasSales && (
              <section className="px-s px-an" style={{ '--d': '0.18s' }}>
                <h3 className="px-sh"><ShoppingCart size={15} /> قنوات البيع والتواصل</h3>
                <div className="px-ch-grid">
                  {p.online_store_url && <ChCard href={p.online_store_url} icon={<Store size={22} />} title="المتجر الإلكتروني" sub="زيارة المتجر" color="#2FAE8F" tid="store" />}
                  {p.instagram_url && <ChCard href={p.instagram_url} icon={<Instagram size={22} />} title="Instagram" sub="عرض الحساب" color="#E1306C" tid="ig" />}
                  {p.snapchat_url && <ChCard href={p.snapchat_url} icon={<Snap s={22} />} title="Snapchat" sub="عرض الحساب" color="#FFFC00" tid="snap" />}
                  {p.mobile_number && <ChCard href={`tel:${p.mobile_number}`} icon={<Phone size={22} />} title={p.mobile_number} sub="اتصال مباشر" color="#2FAE8F" tid="phone" dir="ltr" />}
                  {channels.map((c, i) => <ChCard key={i} href={c.url} icon={<Globe size={22} />} title={c.label || c.channel_type} color="#2FAE8F" tid={`ch-${i}`} />)}
                </div>
              </section>
            )}

            {/* ── 7. PRODUCTS ── */}
            {products.length > 0 && (
              <section className="px-s px-an" style={{ '--d': '0.22s' }}>
                <h3 className="px-sh"><Package size={15} /> المنتجات</h3>
                <div className="px-prod-grid">
                  {products.map((pr, i) => (
                    <div key={i} className="px-prod-card" data-testid={`pf-product-${i}`}>
                      {(pr.image || pr.product_image) ? <img src={getImageUrl(pr.image || pr.product_image)} alt="" className="px-prod-card-img" loading="lazy" /> : <div className="px-prod-card-noimg"><Package size={28} /></div>}
                      <div className="px-prod-card-body">
                        <span className="px-prod-card-name">{pr.name_ar || pr.product_name}</span>
                        {pr.description_ar && <span className="px-prod-card-desc">{pr.description_ar}</span>}
                        {pr.price && <span className="px-prod-card-price">{pr.price} ر.س</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ── 9. PDF ── */}
            {p.project_profile_pdf && (
              <section className="px-s px-an" style={{ '--d': '0.26s' }}>
                <h3 className="px-sh"><FileText size={15} /> الملف التعريفي</h3>
                <div className="px-pdf-card" data-testid="pf-pdf-card">
                  <FileText size={36} className="px-pdf-icon" />
                  <div className="px-pdf-info">
                    <span className="px-pdf-title">الملف التعريفي للمشروع</span>
                    <span className="px-pdf-type">PDF</span>
                  </div>
                  <div className="px-pdf-btns">
                    <a href={getFileUrl(p.project_profile_pdf)} target="_blank" rel="noopener noreferrer" className="px-pdf-btn" data-testid="pf-pdf-view"><Eye size={15} /> عرض</a>
                    <a href={getFileUrl(p.project_profile_pdf)} download className="px-pdf-btn px-pdf-btn-dl" data-testid="pf-pdf-download"><Download size={15} /> تحميل</a>
                  </div>
                </div>
              </section>
            )}

            {/* ── Notes ── */}
            {p.notes && (
              <section className="px-s px-an" style={{ '--d': '0.34s' }}>
                <h3 className="px-sh">ملاحظات</h3>
                <p className="px-sp">{p.notes}</p>
              </section>
            )}

            {/* ── CTA ── */}
            {(p.latitude && p.longitude) && (
              <div className="px-cta-row px-an" style={{ '--d': '0.38s' }}>
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${p.latitude},${p.longitude}`} target="_blank" rel="noopener noreferrer" className="px-cta-go" data-testid="pf-navigate">
                  <Navigation size={18} /> الانتقال لموقع المشروع
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
      <Lightbox />
    </>
  );

  /* ═══ SUMMARY POPUP ═══ */
  return (
    <>
      <div className="px-overlay" data-testid="project-modal-backdrop" onClick={onClose} />
      <div className="px-popup" dir="rtl" data-testid="project-modal">
        <button className="px-x px-x-sm" data-testid="project-modal-close" onClick={onClose}><X size={16} /></button>
        <div className="px-pop-top">
          <div className="px-pop-logo">{logo ? <img src={logo} alt="" /> : <div className="px-pop-logo-fb" style={{ background: catColor }}><Tag size={18} color="#fff" /></div>}</div>
          <div className="px-pop-title-area">
            <h2 className="px-pop-name" data-testid="project-modal-name">{name}</h2>
            <div className="px-pop-meta">
              {cat && <span className="px-pop-cat" style={{ borderColor: `${catColor}44`, color: catColor }}>{cat}</span>}
              {subcat && <span className="px-pop-subcat">{subcat}</span>}
              {featured && <Star size={13} fill="#D6A84F" color="#D6A84F" style={{ flexShrink: 0 }} />}
            </div>
          </div>
        </div>
        <div className="px-pop-mid">
          {(shortD || desc) && <p className="px-pop-desc">{shortD || (desc.length > 180 ? desc.slice(0, 180) + '...' : desc)}</p>}
          <div className="px-pop-loc">
            {region && <span><Building2 size={12} />{region}</span>}
            {city && <span><MapPin size={12} />{city}</span>}
            {district && <span><Home size={12} />{district}</span>}
          </div>
        </div>
        {hasSales && (
          <div className="px-pop-icons">
            {p.online_store_url && <a href={p.online_store_url} target="_blank" rel="noopener noreferrer" className="px-pop-icon-btn" title="المتجر"><Store size={17} /></a>}
            {p.instagram_url && <a href={p.instagram_url} target="_blank" rel="noopener noreferrer" className="px-pop-icon-btn" title="Instagram"><Instagram size={17} /></a>}
            {p.snapchat_url && <a href={p.snapchat_url} target="_blank" rel="noopener noreferrer" className="px-pop-icon-btn" title="Snapchat"><Snap s={17} /></a>}
            {p.mobile_number && <a href={`tel:${p.mobile_number}`} className="px-pop-icon-btn" title={p.mobile_number}><Phone size={17} /></a>}
            {hasSalesLoc && <a href={`https://www.google.com/maps/dir/?api=1&destination=${p.sales_location_lat},${p.sales_location_lng}`} target="_blank" rel="noopener noreferrer" className="px-pop-icon-btn" title="موقع البيع"><MapPin size={17} /></a>}
          </div>
        )}
        <div className="px-pop-btns">
          <button className="px-pop-btn-details" data-testid="project-details-btn" onClick={() => setView('details')}><Eye size={15} /> عرض التفاصيل</button>
          <button className="px-pop-btn-nav" data-testid="project-goto-btn" onClick={() => { if (p.latitude && p.longitude) window.open(`https://www.google.com/maps/dir/?api=1&destination=${p.latitude},${p.longitude}`, '_blank'); }} disabled={!p.latitude || !p.longitude}><Navigation size={15} /> الموقع</button>
        </div>
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════════════════
 * STORY PREMIUM DETAIL — Matches reference design exactly
 * ═══════════════════════════════════════════════════════ */

const SUPPORT_CHIP_ICONS = {
  'تمويل': '💰', 'تدريب': '🎓', 'استشارات': '📋', 'جادة 30': '🏪',
  'منافذ بيع': '🛍️', 'برامج': '💻',
};

const resolveImg = (src) => {
  if (!src) return '';
  if (src.startsWith('http')) return src;
  return getImageUrl(src);
};

const StoryPremiumDetail = ({ stName, stHook, stChallenge, stMetrics, stSupport, stCity, stRegion, stSector, stCover, stGallery, galleryTags, relProj, s, onClose, onBack, onViewProject, lightbox, setLightbox }) => {

  const metrics = Array.isArray(stMetrics) ? stMetrics.slice(0, 4) : [];
  const hasGallery = stGallery && stGallery.length > 0;
  const g = stGallery || [];

  return ReactDOM.createPortal(
    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,zIndex:2147483647,background:'#0A0E1A',overflowY:'auto',overflowX:'hidden',direction:'rtl',fontFamily:'var(--font-arabic)',WebkitOverflowScrolling:'touch'}} data-testid="story-premium-detail">

      {/* Close */}
      <button style={{position:'fixed',top:20,left:20,zIndex:2147483647,width:36,height:36,borderRadius:'50%',border:'1px solid rgba(255,255,255,0.08)',background:'rgba(0,0,0,0.5)',backdropFilter:'blur(8px)',color:'rgba(255,255,255,0.5)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}} data-testid="sp-close" onClick={onClose}><X size={16} /></button>

      {/* ═══ 1. HERO — 75vh, centered, numbers → hook → title ═══ */}
      <div style={{position:'relative',width:'100%',height:'75vh',minHeight:480,overflow:'hidden',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',textAlign:'center'}}>
        <img src={stCover} alt="" style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover'}} />
        <div style={{position:'absolute',inset:0,background:'linear-gradient(180deg, rgba(10,14,26,0.4) 0%, rgba(10,14,26,0.6) 50%, rgba(10,14,26,0.95) 100%)'}} />

        <div style={{position:'relative',zIndex:2,maxWidth:800,padding:'0 32px',display:'flex',flexDirection:'column',alignItems:'center'}}>
          {/* Numbers — BIGGEST */}
          {metrics.length > 0 && (
            <div style={{display:'flex',gap:28,justifyContent:'center',flexWrap:'wrap',marginBottom:24}} data-testid="sp-hero-metrics">
              {metrics.map((m, i) => (
                <div key={i} style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                  <span style={{fontFamily:'Outfit, sans-serif',fontSize: i === 0 ? 'clamp(48px, 8vw, 72px)' : 'clamp(36px, 6vw, 56px)',fontWeight:900,color:'#E8A838',lineHeight:1,direction:'ltr',textShadow:'0 4px 30px rgba(232,168,56,0.25)'}}>{m.value}</span>
                  <span style={{fontSize:13,fontWeight:600,color:'rgba(255,255,255,0.4)',marginTop:4}}>{m.label}</span>
                </div>
              ))}
            </div>
          )}
          {/* Hook — centered */}
          <h1 style={{fontSize:'clamp(22px, 3.5vw, 36px)',fontWeight:800,lineHeight:1.4,color:'#fff',margin:0,textShadow:'0 2px 30px rgba(0,0,0,0.5)'}} data-testid="sp-hook">{stHook || stName}</h1>
        </div>
      </div>

      {/* ═══ CONTENT ═══ */}
      <div style={{maxWidth:960,margin:'0 auto',padding:'0 32px 80px'}}>

        {/* ═══ 2. CHALLENGE — badge + text centered ═══ */}
        {stChallenge && (
          <div style={{textAlign:'center',padding:'48px 0 32px'}}>
            <div style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 16px',borderRadius:20,border:'1px solid rgba(232,168,56,0.15)',background:'rgba(232,168,56,0.06)',color:'#E8A838',fontSize:11,fontWeight:700,marginBottom:16}}><Target size={14} /> التحدي</div>
            <p style={{fontSize:'clamp(18px, 2.5vw, 26px)',fontWeight:700,lineHeight:1.6,color:'rgba(255,255,255,0.75)',margin:0,maxWidth:600,marginLeft:'auto',marginRight:'auto'}}>{stChallenge}</p>
          </div>
        )}

        {/* ═══ 3. TWO-COLUMN: Image + Challenge/Support ═══ */}
        {(hasGallery || stSupport.length > 0) && (
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginTop:16}} data-testid="sp-info-strip">
            {/* Left: Large image */}
            <div style={{borderRadius:14,overflow:'hidden',position:'relative',minHeight:320,cursor:'pointer',background:'#111'}} onClick={() => setLightbox(0)}>
              {g[0] && <img src={resolveImg(g[0])} alt="" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} />}
              {galleryTags[0] && <div style={{position:'absolute',bottom:12,right:12,display:'flex',alignItems:'center',gap:4,fontSize:11,color:'rgba(255,255,255,0.5)'}}><Heart size={12} /> {galleryTags[0]}</div>}
            </div>

            {/* Right: Challenge info + chips + second image */}
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <div style={{padding:'20px 0'}}>
                <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:8}}>
                  <Target size={14} style={{color:'#E8A838'}} />
                  <span style={{fontSize:13,fontWeight:700,color:'#E8A838'}}>التحدي</span>
                </div>
                <p style={{fontSize:13,fontWeight:500,lineHeight:1.7,color:'rgba(255,255,255,0.4)',margin:0}}>{stChallenge}</p>
                {/* Support chips */}
                {stSupport.length > 0 && (
                  <div style={{display:'flex',flexWrap:'wrap',gap:8,marginTop:14}}>
                    {stSupport.map((t, i) => (
                      <span key={i} style={{display:'inline-flex',alignItems:'center',gap:4,padding:'6px 14px',borderRadius:20,background: i === 0 ? 'rgba(47,174,143,0.15)' : 'rgba(255,255,255,0.06)',border: i === 0 ? '1px solid rgba(47,174,143,0.2)' : '1px solid rgba(255,255,255,0.08)',fontSize:12,fontWeight:600,color: i === 0 ? '#2FAE8F' : 'rgba(255,255,255,0.5)'}}>{SUPPORT_CHIP_ICONS[t] || '✦'} {t}</span>
                    ))}
                  </div>
                )}
              </div>
              {/* Second image */}
              {g[1] && (
                <div style={{borderRadius:14,overflow:'hidden',position:'relative',flex:1,minHeight:180,cursor:'pointer',background:'#111'}} onClick={() => setLightbox(1)}>
                  <img src={resolveImg(g[1])} alt="" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} />
                  {galleryTags[1] && <div style={{position:'absolute',bottom:12,right:12,display:'flex',alignItems:'center',gap:4,fontSize:11,color:'rgba(255,255,255,0.5)'}}><Heart size={12} /> {galleryTags[1]}</div>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ 4. JOURNEY — title + 2-col images + project card ═══ */}
        <div style={{marginTop:48}}>
          <h3 style={{textAlign:'center',fontSize:20,fontWeight:700,color:'rgba(255,255,255,0.7)',marginBottom:24}}>رحلة المشروع</h3>
          <div style={{display:'grid',gridTemplateColumns: relProj ? '1fr 1fr 1fr' : '1fr 1fr',gap:16}} data-testid="sp-journey">
            {/* Journey image 1 */}
            {g[0] && (
              <div style={{borderRadius:14,overflow:'hidden',position:'relative',minHeight:220,cursor:'pointer',background:'#111'}} onClick={() => setLightbox(0)}>
                <img src={resolveImg(g[0])} alt="" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} />
                <div style={{position:'absolute',bottom:10,right:10,display:'flex',alignItems:'center',gap:4,fontSize:10,color:'rgba(255,255,255,0.45)'}}><Heart size={11} /> {galleryTags[0] || 'بعد'}</div>
              </div>
            )}
            {/* Journey image 2 */}
            {g[2] && (
              <div style={{borderRadius:14,overflow:'hidden',position:'relative',minHeight:220,cursor:'pointer',background:'#111'}} onClick={() => setLightbox(2)}>
                <img src={resolveImg(g[2])} alt="" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} />
                <div style={{position:'absolute',bottom:10,right:10,display:'flex',alignItems:'center',gap:4,fontSize:10,color:'rgba(255,255,255,0.45)'}}><Heart size={11} /> {galleryTags[2] || 'بعد'}</div>
              </div>
            )}
            {/* Related project card (inline in grid) */}
            {relProj && (
              <div style={{borderRadius:14,border:'1px solid rgba(255,255,255,0.06)',background:'rgba(255,255,255,0.02)',padding:24,display:'flex',flexDirection:'column',justifyContent:'center'}} data-testid="sp-project">
                <h4 style={{fontSize:16,fontWeight:700,color:'rgba(255,255,255,0.6)',margin:'0 0 4px',textAlign:'right'}}>عن المشروع</h4>
                <p style={{fontSize:14,fontWeight:800,color:'rgba(255,255,255,0.8)',margin:'8px 0 0',display:'flex',alignItems:'center',gap:6,justifyContent:'flex-end'}}><MapPin size={13} /> {relProj.project_name_ar}</p>
                <span style={{fontSize:11,color:'rgba(255,255,255,0.25)',textAlign:'right',display:'block',marginTop:2}}>{[stCity || relProj.city_name, stSector || relProj.category_name].filter(Boolean).join(' · ')}</span>
                {relProj.short_description && <p style={{fontSize:12,color:'rgba(255,255,255,0.3)',margin:'10px 0 0',lineHeight:1.6,textAlign:'right'}}>{relProj.short_description}</p>}
                <button onClick={() => { if(onViewProject){onClose();setTimeout(()=>onViewProject({id:relProj.id,project_name_ar:relProj.project_name_ar}),150);} }}
                  style={{display:'inline-flex',alignItems:'center',gap:5,padding:'8px 20px',borderRadius:10,border:'none',background:'rgba(47,174,143,0.15)',color:'#2FAE8F',fontSize:12,fontWeight:700,cursor:'pointer',marginTop:14,alignSelf:'flex-end'}} data-testid="sp-view-project">
                  <Eye size={13} /> استكشف المشروع
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ═══ LIGHTBOX ═══ */}
      {lightbox >= 0 && g[lightbox] && (
        <div style={{position:'fixed',inset:0,zIndex:2147483647,background:'rgba(0,0,0,0.92)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}}
          onClick={() => setLightbox(-1)} data-testid="sp-lightbox">
          <img src={resolveImg(g[lightbox])} alt="" style={{maxWidth:'90vw',maxHeight:'85vh',objectFit:'contain',borderRadius:12}} onClick={e => e.stopPropagation()} />
          <button style={{position:'absolute',top:20,left:20,width:40,height:40,borderRadius:'50%',border:'1px solid rgba(255,255,255,.15)',background:'rgba(0,0,0,.5)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}} onClick={() => setLightbox(-1)}><X size={18} /></button>
          {g.length > 1 && <>
            <button style={{position:'absolute',top:'50%',right:20,transform:'translateY(-50%)',width:44,height:44,borderRadius:'50%',border:'1px solid rgba(255,255,255,.1)',background:'rgba(0,0,0,.4)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}} onClick={e => { e.stopPropagation(); setLightbox((lightbox - 1 + g.length) % g.length); }}><ChevronRight size={22} /></button>
            <button style={{position:'absolute',top:'50%',left:20,transform:'translateY(-50%)',width:44,height:44,borderRadius:'50%',border:'1px solid rgba(255,255,255,.1)',background:'rgba(0,0,0,.4)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}} onClick={e => { e.stopPropagation(); setLightbox((lightbox + 1) % g.length); }}><ChevronLeft size={22} /></button>
          </>}
        </div>
      )}
    </div>,
    document.body
  );
};

const ChCard = ({ href, icon, title, sub, color, tid, dir }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="px-ch-card" data-testid={`pf-ch-${tid}`}>
    <span className="px-ch-card-icon" style={{ '--chc': color }}>{icon}</span>
    <span className="px-ch-card-title" dir={dir}>{title}</span>
    {sub && <span className="px-ch-card-sub">{sub}</span>}
    <ExternalLink size={12} className="px-ch-card-arrow" />
  </a>
);

/* ── Support type icon mapper ── */
const SUPPORT_ICONS = {
  'تمويل': '💰', 'تدريب': '🎓', 'حاضنات': '🏢', 'حاضنات إنتاجية': '🏭', 'حاضنات تقنية': '💻', 'حاضنات صناعية': '🏗',
  'حاضنات حرفية': '🧶', 'دعم تسويقي': '📢', 'دعم تقني': '⚙', 'استشارات': '📋', 'استشارات تجارية': '📋',
  'استشارات زراعية': '🌱', 'استشارات بيئية': '🌍', 'استشارات صحية': '🩺', 'استشارات هندسية': '📐',
  'استشارات سياحية': '🧭', 'منفذ بيع': '🏪', 'شراكات أكاديمية': '🎓', 'شراكات مصرفية': '🏦',
  'شراكات صحية': '🏥', 'شراكات سياحية': '✈', 'تدريب متخصص': '🎯', 'تدريب فني': '🔧',
  'حماية المنتج الوطني': '🛡',
};
const SupportIcon = ({ type }) => {
  const icon = Object.entries(SUPPORT_ICONS).find(([k]) => type.includes(k));
  return <span className="ss-pill-icon">{icon ? icon[1] : '✦'}</span>;
};

/* ── Count-up number animation (English numerals) ── */
const CountUp = ({ value, prefix = '' }) => {
  const ref = useRef(null);
  const num = parseInt(value) || 0;
  useEffect(() => {
    const el = ref.current;
    if (!el || !num) return;
    const duration = 1600;
    const startTime = performance.now();
    const step = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      const current = Math.round(eased * num);
      el.textContent = prefix + current.toLocaleString('en-US');
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [num, prefix]);
  return <span ref={ref} className="ss-metric-num" dir="ltr">{prefix}{num.toLocaleString('en-US')}</span>;
};

/* ── Story metrics parser: creative cards with English numbers ── */
const METRIC_ICONS = {
  'طن': '&#9878;', 'موظف': '&#128101;', 'فرع': '&#127970;', 'دولة': '&#127758;',
  'متدرب': '&#127891;', 'طالب': '&#127891;', 'مستخدم': '&#128241;', 'جائزة': '&#127942;',
  'مليون': '&#128176;', 'ألف': '&#128176;', 'ريال': '&#128176;', 'سائح': '&#9992;',
  'رحلة': '&#9992;', 'تصميم': '&#127912;', 'شراكة': '&#129309;',
};
const getMetricIcon = (label) => {
  for (const [key, icon] of Object.entries(METRIC_ICONS)) {
    if (label.includes(key)) return icon;
  }
  return '&#10022;';
};

const StoryMetrics = ({ text }) => {
  if (!text) return null;
  // Handle new structured array format [{label, value, highlight}]
  if (Array.isArray(text)) {
    if (text.length === 0) return null;
    return (
      <div className="ss-metrics-wrap">
        {text.slice(0, 4).map((m, i) => {
          const val = String(m.value || '').replace(/[+\s]/g, '');
          return (
            <div key={i} className={`ss-metric-card ${m.highlight ? 'ss-metric-card-hero' : ''}`}>
              <span className="ss-metric-icon" dangerouslySetInnerHTML={{ __html: getMetricIcon(m.label || '') }} />
              <CountUp value={val} prefix="+" />
              <span className="ss-metric-label">{m.label || ''}</span>
            </div>
          );
        })}
      </div>
    );
  }
  // Legacy string format
  const parts = text.split(/[,،]/g).map(s => s.trim()).filter(Boolean);
  if (parts.length <= 1) {
    const numMatch = text.match(/(\d[\d,٬.]*)/);
    if (numMatch) {
      const numStr = numMatch[1].replace(/[,٬]/g, '');
      const label = text.replace(numMatch[0], '').trim();
      return (
        <div className="ss-metrics-wrap">
          <div className="ss-metric-card ss-metric-card-hero">
            <span className="ss-metric-icon" dangerouslySetInnerHTML={{ __html: getMetricIcon(label) }} />
            <CountUp value={numStr} prefix="+" />
            <span className="ss-metric-label">{label}</span>
          </div>
        </div>
      );
    }
    return <p className="px-sp ss-metrics-text">{text}</p>;
  }
  return (
    <div className="ss-metrics-wrap">
      {parts.map((part, i) => {
        const numMatch = part.match(/(\d[\d,٬.]*)/);
        if (numMatch) {
          const numStr = numMatch[1].replace(/[,٬]/g, '');
          const label = part.replace(numMatch[0], '').trim();
          return (
            <div key={i} className={`ss-metric-card ${i === 0 ? 'ss-metric-card-hero' : ''}`}>
              <span className="ss-metric-icon" dangerouslySetInnerHTML={{ __html: getMetricIcon(label) }} />
              <CountUp value={numStr} prefix="+" />
              <span className="ss-metric-label">{label}</span>
            </div>
          );
        }
        return (
          <div key={i} className="ss-metric-card ss-metric-card-text">
            <span className="ss-metric-icon" dangerouslySetInnerHTML={{ __html: '&#127942;' }} />
            <span className="ss-metric-label ss-metric-label-full">{part}</span>
          </div>
        );
      })}
    </div>
  );
};

export default ProjectDetailPanel;
